import React, {memo} from "react";
import {get} from "lodash";
import {UserPermissionEnum} from "../../../utils/types/user";

import {BarChart} from "../../../atoms/admin-reporting/atoms/bar-chart";
import {
  AggregationType,
  AnalyticsType,
  ReportingContextType,
} from "../../../store/reporting/reporting.common";
import {RequiredUserPermission} from "../../../atoms/admin-user-permissions/RequiredUserPermission";
import {ReportingContext} from "../ReportingContext";
import {
  ChartProps,
  Props,
  useOnDataPathChange,
  dataProcess_AnalyticsPercentageByFacility,
  numberToPercent,
  withCompare,
} from "./helpers";
import {CardTitle} from "../../../atoms/card-title";
import {CompareDateLabel} from "../../../atoms/reporting-compare";
import {CompareDateLabelsWrapper} from "../reports/Report.common";

const AnalyticsAdmissionsFacilityCompletionRateBarChart = memo(
  ({data, isFullReport}: ChartProps) => {
    const {stack, max} = dataProcess_AnalyticsPercentageByFacility(data);

    return (
      <BarChart
        small
        data={stack}
        max={max}
        full={isFullReport}
        labelLeft="Facilities"
        labelRight="Completion Rate"
        dataLegend={[{name: "Percentage of Approved Admissions"}]}
        axTickFormat={v => numberToPercent(v)}
        hoverDataPreprocessor={(data, line, index) => {
          const sum = get(
            line.original[index].aggregations,
            AggregationType.SUM,
            0,
          );
          const count = get(
            line.original[index].aggregations,
            AggregationType.COUNT,
            0,
          );

          return [`${sum}/${count}`];
        }}
      />
    );
  },
);

export const FACILITY_COMPLETION_RATE_CHART_ID =
  "facility-completion-rate" as const;

export const AnalyticsAdmissionsFacilityCompletionRate = (props: Props) => (
  <RequiredUserPermission
    permission={UserPermissionEnum.ADMISSION_COMPLETION_RATE}
  >
    <div className="chart-header">
      <CardTitle
        title="Workflow Completion Rate Per Facility"
        subtitle="by Facility"
      />
      <CompareDateLabelsWrapper>
        <CompareDateLabel dateRange={props.dateRange} global />
      </CompareDateLabelsWrapper>
    </div>
    <ReportingContext
      dataPaths={withCompare({
        what: ReportingContextType.analyticsWithoutResolution,
        type: AnalyticsType.admissionsFacilityCompletionRate,
        facilities: props.facilities,
        query: {...props.dateRange},
      })}
      onDataPathChange={useOnDataPathChange(props.reportingBlockSetters)}
      isFullReport={props.reportingBlockSetters.isFullReport}
      enabled={props.reportingBlockSetters.enabled}
    >
      {(data, isFullReport) => (
        <AnalyticsAdmissionsFacilityCompletionRateBarChart
          data={data}
          isFullReport={isFullReport}
        />
      )}
    </ReportingContext>
  </RequiredUserPermission>
);
